import * as THREE from "three";
import type { Loader } from "../../assets/loader";
import { ArtworkWrapper } from "@/types/artworksData";
import {
    ARTWORK_CANVAS_TITLE_WIDTH,
    ARTWORK_CANVAS_TITLE_HEIGHT,
    ARTWORK_CANVAS_TITLE_FONT,
    ARTWORK_CANVAS_TITLE_FILL_STYLE,
    ARTWORK_CANVAS_TITLE_STROKE_STYLE,
    ARTWORK_CANVAS_TITLE_Y_POSITION,
    ARROW_IMAGE_URL
} from "../artwork.constants";

type ArtworkLabelLayout = "default" | "pseudoMoMFixed";

type ArtworkLabelOptions = {
    layout?: ArtworkLabelLayout;
    showArrows?: boolean;
};

const PSEUDO_MOM_CANVAS_TITLE_FILL_STYLE = "#000000";
const PSEUDO_MOM_CANVAS_TITLE_STROKE_STYLE = "rgba(255,255,255,0.65)";
const PSEUDO_MOM_LABEL_MAX_FONT_SIZE = 176;
const PSEUDO_MOM_LABEL_MIN_FONT_SIZE = 96;
const PSEUDO_MOM_LABEL_FONT_STEP = 4;
const PSEUDO_MOM_LABEL_HORIZONTAL_PADDING = 160;
const PSEUDO_MOM_LABEL_BOTTOM_PADDING = 60;

type ArtworkLabelTextLayout = {
    lines: string[];
    font: string;
    fontSize: number;
    lineHeight: number;
};

export function resolveArtworkLabelCanvasStyles(layout: ArtworkLabelLayout = "default") {
    return layout === "pseudoMoMFixed"
        ? {
            fillStyle: PSEUDO_MOM_CANVAS_TITLE_FILL_STYLE,
            strokeStyle: PSEUDO_MOM_CANVAS_TITLE_STROKE_STYLE,
        }
        : {
            fillStyle: ARTWORK_CANVAS_TITLE_FILL_STYLE,
            strokeStyle: ARTWORK_CANVAS_TITLE_STROKE_STYLE,
        };
}

const withCanvasFontSize = (font: string, fontSize: number): string =>
    font.replace(/\d+(?:\.\d+)?px/, `${fontSize}px`);

const measureTextWidth = (ctx: CanvasRenderingContext2D, text: string): number =>
    ctx.measureText(text).width;

const truncateCanvasLine = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string => {
    const raw = String(text || "").trim();
    if (measureTextWidth(ctx, raw) <= maxWidth) {
        return raw;
    }

    const ellipsis = "...";
    const ellipsisWidth = measureTextWidth(ctx, ellipsis);
    let truncated = raw;
    while (truncated.length > 0 && measureTextWidth(ctx, truncated) + ellipsisWidth > maxWidth) {
        truncated = truncated.slice(0, -1).trimEnd();
    }
    return truncated ? `${truncated}${ellipsis}` : ellipsis;
};

const wrapCanvasTitle = (
    ctx: CanvasRenderingContext2D,
    title: string,
    maxWidth: number,
    maxLines: number
): { lines: string[]; fits: boolean } => {
    const words = String(title || "").trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) {
        return { lines: [""], fits: true };
    }

    const lines: string[] = [];
    let currentLine = "";

    for (let index = 0; index < words.length; index += 1) {
        const word = words[index];
        const candidate = currentLine ? `${currentLine} ${word}` : word;

        if (!currentLine || measureTextWidth(ctx, candidate) <= maxWidth) {
            currentLine = candidate;
            continue;
        }

        lines.push(currentLine);
        currentLine = word;

        if (lines.length === maxLines) {
            const remaining = [currentLine, ...words.slice(index + 1)].join(" ");
            lines[maxLines - 1] = remaining;
            return { lines, fits: false };
        }
    }

    if (currentLine) {
        lines.push(currentLine);
    }

    return {
        lines,
        fits: lines.length <= maxLines && lines.every((line) => measureTextWidth(ctx, line) <= maxWidth),
    };
};

export const resolveArtworkLabelTextLayout = (
    ctx: CanvasRenderingContext2D,
    title: string,
    layout: ArtworkLabelLayout = "default"
): ArtworkLabelTextLayout => {
    if (layout !== "pseudoMoMFixed") {
        ctx.font = ARTWORK_CANVAS_TITLE_FONT;
        const maxWidth = ARTWORK_CANVAS_TITLE_WIDTH;
        const ellipsis = "...";
        const ellipsisWidth = measureTextWidth(ctx, ellipsis);
        let text = String(title || "").trim();
        while (measureTextWidth(ctx, text) + ellipsisWidth > maxWidth && text.length > 0) {
            text = text.slice(0, -1);
        }
        if (text !== String(title || "").trim()) {
            text += ellipsis;
        }
        return {
            lines: [text],
            font: ARTWORK_CANVAS_TITLE_FONT,
            fontSize: Number.parseFloat(ARTWORK_CANVAS_TITLE_FONT) || 240,
            lineHeight: 0,
        };
    }

    const maxWidth = ARTWORK_CANVAS_TITLE_WIDTH - (PSEUDO_MOM_LABEL_HORIZONTAL_PADDING * 2);
    for (
        let fontSize = PSEUDO_MOM_LABEL_MAX_FONT_SIZE;
        fontSize >= PSEUDO_MOM_LABEL_MIN_FONT_SIZE;
        fontSize -= PSEUDO_MOM_LABEL_FONT_STEP
    ) {
        const font = withCanvasFontSize(ARTWORK_CANVAS_TITLE_FONT, fontSize);
        ctx.font = font;
        const wrapped = wrapCanvasTitle(ctx, title, maxWidth, 2);
        if (wrapped.fits) {
            return {
                lines: wrapped.lines,
                font,
                fontSize,
                lineHeight: fontSize * 0.9,
            };
        }
    }

    const font = withCanvasFontSize(ARTWORK_CANVAS_TITLE_FONT, PSEUDO_MOM_LABEL_MIN_FONT_SIZE);
    ctx.font = font;
    const wrapped = wrapCanvasTitle(ctx, title, maxWidth, 2);
    return {
        lines: wrapped.lines.slice(0, 2).map((line) => truncateCanvasLine(ctx, line, maxWidth)),
        font,
        fontSize: PSEUDO_MOM_LABEL_MIN_FONT_SIZE,
        lineHeight: PSEUDO_MOM_LABEL_MIN_FONT_SIZE * 0.9,
    };
};

export class ArtworkLabel {
    private static readonly PSEUDO_MOM_FIXED_Y = 0.55;
    private static readonly PSEUDO_MOM_FIXED_Z = 0.3;
    private static readonly PSEUDO_MOM_ARROW_X = 1.20;
    private static readonly PSEUDO_MOM_ARROW_SIZE = 0.20;
    private static readonly PSEUDO_MOM_TITLE_Y = -1.40;

    /**
     * Creates and adds a label to an artwork
     * Includes title text and navigation arrows
     * @param loader Asset loader instance
     * @param artworkData Artwork configuration
     * @returns Promise resolving to the created sprite
     */
    static addArtworkLabel(loader: Loader, artworkData: ArtworkWrapper, options: ArtworkLabelOptions = {}) {
        return new Promise((resolve) => {
            //create image
            var bitmap = document.createElement('canvas');
            var ctx = bitmap.getContext('2d');
            bitmap.width = ARTWORK_CANVAS_TITLE_WIDTH;
            bitmap.height = ARTWORK_CANVAS_TITLE_HEIGHT;
            const canvasStyles = resolveArtworkLabelCanvasStyles(options.layout || "default");
            ctx.fillStyle = canvasStyles.fillStyle;

            ctx.strokeStyle = canvasStyles.strokeStyle;
            ctx.lineWidth = 0;

            const title = artworkData.artworks[artworkData.activeArtwork].title;
            const textLayout = resolveArtworkLabelTextLayout(ctx, title, options.layout || "default");
            ctx.font = textLayout.font;
            const bottomY = ARTWORK_CANVAS_TITLE_HEIGHT - (
                options.layout === "pseudoMoMFixed" ? PSEUDO_MOM_LABEL_BOTTOM_PADDING : 40
            );
            const firstY = bottomY - ((textLayout.lines.length - 1) * textLayout.lineHeight);
            textLayout.lines.forEach((line, index) => {
                const lineWidth = ctx.measureText(line).width;
                const x = (ARTWORK_CANVAS_TITLE_WIDTH / 2) - (lineWidth / 2);
                const y = firstY + (index * textLayout.lineHeight);
                ctx.fillText(line, x, y);
                ctx.strokeText(line, x, y);
            });

            var texture = new THREE.Texture(bitmap)
            texture.needsUpdate = true;

            const material = new THREE.SpriteMaterial({ map: texture, depthWrite: false });
            const sprite = new THREE.Sprite(material);

            sprite.scale.set(1, 0.2, 1)
            sprite.position.y = ARTWORK_CANVAS_TITLE_Y_POSITION

            if (options.layout === "pseudoMoMFixed") {
                const group = new THREE.Group();
                group.position.set(0, ArtworkLabel.PSEUDO_MOM_FIXED_Y, ArtworkLabel.PSEUDO_MOM_FIXED_Z);

                material.transparent = true;
                material.alphaTest = Math.max(material.alphaTest || 0, 0.01);
                material.depthTest = false;
                material.depthWrite = false;
                sprite.position.set(0, ArtworkLabel.PSEUDO_MOM_TITLE_Y, 0);
                group.add(sprite);

                if (options.showArrows === false) {
                    resolve(group);
                    return;
                }

                loader.loadTexture(ARROW_IMAGE_URL).then((textureArrow: THREE.Texture | null) => {
                    const textureImage = textureArrow?.image || (textureArrow as any)?.source?.data;
                    if (!textureArrow || (textureArrow as any).isDisposed === true || !textureImage) {
                        resolve(group);
                        return;
                    }
                    textureArrow.needsUpdate = true;
                    const arrowGeometry = new THREE.PlaneGeometry(ArtworkLabel.PSEUDO_MOM_ARROW_SIZE, ArtworkLabel.PSEUDO_MOM_ARROW_SIZE);

                    const materialArrow = new THREE.MeshBasicMaterial({
                        map: textureArrow,
                        transparent: true,
                        alphaTest: 0.01,
                        depthTest: false,
                        depthWrite: false,
                        side: THREE.DoubleSide
                    });
                    const spriteArrow = new THREE.Mesh(arrowGeometry, materialArrow);
                    spriteArrow.rotation.z = Math.PI;
                    spriteArrow.position.set(-ArtworkLabel.PSEUDO_MOM_ARROW_X, 0, 0);
                    spriteArrow.userData = { ...artworkData, isSprite: true, isDown: true, isUp: false };
                    group.add(spriteArrow);

                    const materialArrow2 = new THREE.MeshBasicMaterial({
                        map: textureArrow,
                        transparent: true,
                        alphaTest: 0.01,
                        depthTest: false,
                        depthWrite: false,
                        side: THREE.DoubleSide
                    });
                    const spriteArrow2 = new THREE.Mesh(arrowGeometry.clone(), materialArrow2);
                    spriteArrow2.position.set(ArtworkLabel.PSEUDO_MOM_ARROW_X, 0, 0);
                    spriteArrow2.userData = { ...artworkData, isSprite: true, isUp: true, isDown: false };
                    group.add(spriteArrow2);

                    resolve(group);
                }).catch(() => resolve(group));
                return;
            }

            loader.loadTexture(ARROW_IMAGE_URL).then((textureArrow: THREE.Texture | null) => {
                if (!textureArrow) {
                    resolve(sprite);
                    return;
                }

                const materialArrow = new THREE.SpriteMaterial({ map: textureArrow });
                materialArrow.rotation = -Math.PI / 2
                const spriteArrow = new THREE.Sprite(materialArrow);
                let scalar = 0.1
                spriteArrow.scale.set(1 * scalar, 1 / 0.2 * scalar, 1 * scalar)
                spriteArrow.position.y = -0.9
                spriteArrow.userData = { ...artworkData, isSprite: true, isDown: true, isUp: false }

                sprite.add(spriteArrow)

                const materialArrow2 = new THREE.SpriteMaterial({ map: textureArrow });
                materialArrow2.rotation = Math.PI / 2
                const spriteArrow2 = new THREE.Sprite(materialArrow2);

                spriteArrow2.scale.set(1 * scalar, 1 / 0.2 * scalar, 1 * scalar)
                spriteArrow2.position.y = 0.3
                spriteArrow2.userData = { ...artworkData, isSprite: true, isUp: true, isDown: false }
                sprite.add(spriteArrow2)
                resolve(sprite)
            })
        })
    }
}
